import { useState, useEffect, useRef } from 'react';
import { listOrgCollection } from '../../storage/FileSystemAdapter';
import IncidentList from './IncidentList';
import IncidentForm from './IncidentForm';
import ConfirmDialog from '../shared/ConfirmDialog';
import { isRecordImmutable } from '../../utils/changeLog';
import { BTN_PRIMARY, BTN_SAVE_AND_LEAVE, TEXT_MUTED, TEXT_DISABLED, TEXT_HEADING, ERROR_BG, ERROR_TEXT, ERROR_BORDER, FONT_PAGE_TITLE, FONT_BUTTON, FONT_LABEL, FONT_SMALL, FONT_TINY } from '../../theme';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:       'Gestión de incidentes',
    backToList:  '← Volver a la lista',
    clauseRef:   'Controles A.5.24–5.27 · ISO/IEC 27001:2022',
    loadError:   'Error al leer los datos. Comprueba el acceso a la carpeta.',
    retry:        'Reintentar',
    newIncident:  '+ Nuevo incidente',
    unsavedLabel:       'Cambios sin guardar',
    unsavedConfirm:     '¿Salir sin guardar los cambios?',
    leaveWithoutSaving: 'Salir sin guardar',
    saveAndLeave:       'Guardar y salir',
    formCancel:         'Cancelar',
    deleteBtn:          'Eliminar',
  },
  en: {
    title:       'Incident management',
    backToList:  '← Back to list',
    clauseRef:   'Controls A.5.24–5.27 · ISO/IEC 27001:2022',
    loadError:   'Failed to read data. Check folder access.',
    retry:        'Retry',
    newIncident:  '+ New incident',
    unsavedLabel:       'Unsaved changes',
    unsavedConfirm:     'Leave without saving changes?',
    leaveWithoutSaving: 'Leave without saving',
    saveAndLeave:       'Save and leave',
    formCancel:         'Cancel',
    deleteBtn:          'Delete',
  },
};

// ── COMPONENT ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function IncidentsView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [incidents,    setIncidents]    = useState(null);
  const [formMode,     setFormMode]     = useState(null);
  const [editIncident, setEditIncident] = useState(null);
  const [loadError,    setLoadError]    = useState(false);
  const [dirty,        setDirty]        = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next successful form save reports back via onSaveResult instead
  // of running the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef               = useRef(false);
  const lastProcessedAppTickRef         = useRef(0);

  useScrollToTopOnFormOpen(formMode !== null);

  const loadIncidents = () => {
    setLoadError(false);
    listOrgCollection(org.id, 'incidents')
      .then((items) => {
        const sorted = [...items].sort((a, b) => (b.detectedAt || '').localeCompare(a.detectedAt || ''));
        setIncidents(sorted);
      })
      .catch(() => { setIncidents([]); setLoadError(true); });
  };

  useEffect(() => {
    loadIncidents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  const handleFormSave = (fileId) => {
    if (fileId === null) {
      setFormMode(null);
      setEditIncident(null);
      setDirty(false);
      setSaveRequestTick(0);
      loadIncidents();
      return;
    }
    setDirty(false);
    setSaveRequestTick(0);
    loadIncidents();
    if (pendingAppSaveRef.current) {
      pendingAppSaveRef.current = false;
      onSaveResult?.(true);
    }
  };

  const closeForm = () => { setFormMode(null); setEditIncident(null); setDirty(false); };

  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          color: BTN_SAVE_AND_LEAVE,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  // ── App-level guard: bridge App tick into local tick to fire the embedded
  //    form's existing saveRequestTick handler. See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (formMode) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload browser guard ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  if (formMode) {
    return (
      <div>
        <button
          onClick={handleFormCancel}
          style={{
            ...FONT_LABEL,
            background: 'none', border: 'none', cursor: 'pointer',
            color: TEXT_MUTED, letterSpacing: '0.03em', padding: 0,
            marginBottom: '0.75rem', display: 'block',
          }}
        >
          {t.backToList}
        </button>
        <IncidentForm
          incident={formMode === 'create' ? null : editIncident}
          mode={formMode}
          org={org}
          lang={lang}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          onFormChange={() => setDirty(true)}
          saveRequestTick={saveRequestTick}
          dirty={dirty}
        />
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel || 'Cancel'}
          secondaryAction={confirmState?.secondaryAction}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div>
          <h1 style={{ ...FONT_PAGE_TITLE, color: TEXT_HEADING, margin: 0 }}>{t.title}</h1>
          <p style={{ ...FONT_TINY, color: TEXT_DISABLED, margin: '0.15rem 0 0', letterSpacing: '0.03em' }}>{t.clauseRef}</p>
        </div>
        <button
          onClick={() => { setFormMode('create'); setDirty(false); }}
          style={{
            ...BTN_PRIMARY,
            ...FONT_BUTTON,
            border: 'none', borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer',
            alignSelf: 'flex-start',
          }}
        >
          {t.newIncident}
        </button>
      </div>
      {/* Load error banner */}
      {loadError && (
        <div style={{
          ...FONT_LABEL,
          background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8,
          padding: '0.75rem 1rem', marginBottom: '1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
          color: ERROR_TEXT,
        }}>
          <span>{t.loadError}</span>
          <button
            onClick={loadIncidents}
            style={{
              ...FONT_SMALL,
              background: 'none', border: `1px solid ${ERROR_BORDER}`, borderRadius: 6,
              padding: '0.25rem 0.6rem', cursor: 'pointer',
              color: ERROR_TEXT, flexShrink: 0,
            }}
          >
            {t.retry}
          </button>
        </div>
      )}
      <IncidentList
        incidents={incidents}
        loading={incidents === null}
        org={org}
        lang={lang}
        onNew={() => { setFormMode('create'); setDirty(false); }}
        onEdit={(inc) => {
          setEditIncident(inc);
          // §11 rule 34 — Closed incidents are immutable; open read-only.
          setFormMode(isRecordImmutable(inc, 'incident') ? 'view' : 'edit');
          setDirty(false);
        }}
        onView={(inc) => { setEditIncident(inc); setFormMode('view'); setDirty(false); }}
        onNavigate={onNavigate}
        onIncidentsChanged={loadIncidents}
      />
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
